import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Space, Flex, Typography, Tag, Button, Spin } from 'antd';

import { useInspectionStore } from '../store/inspection.store';
import useRemoteControl from '@/features/boxes/components/RemoteControl/hook/useRemoteControl';

const { Text } = Typography;

// Starts direct check active mode for 2min
const INTERVAL = 3000;
const DURATION_MAX = 20; // 2 minutes
const DURATION_RECONNECT = 10; // durée reconnexion en secondes

export const BoxStatus = ({
  lpui,
  isStarted,
}: {
  lpui: string;
  isStarted: boolean;
}) => {
  const [loader, setLoader] = useState(false);
  const { getConnectivity } = useRemoteControl(lpui);
  const setBoxIsConnected = useInspectionStore(
    (state) => state.setBoxIsConnected
  );
  const boxIsConnected = useInspectionStore((state) => state.boxIsConnected);
  const [reconnectMode, setReconnectMode] = useState(false);

  // Utiliser useRef pour stocker l'intervalle et le timestamp de départ
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const intervalStartRef = useRef<number | null>(null);

  const connection = useCallback(async () => {
    const data = await getConnectivity();
    setBoxIsConnected(data?.is_connected, data?.is_direct_mode);
  }, [getConnectivity, setBoxIsConnected]);

  useEffect(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    // Détermine la durée selon le mode
    const duration = reconnectMode ? DURATION_RECONNECT : DURATION_MAX;

    // On relance la connexion si isStarted ou reconnectMode
    if (!isStarted && !reconnectMode) {
      setLoader(false);

      return;
    }

    intervalStartRef.current = Date.now() / 1000;
    setLoader(true);

    intervalIdRef.current = setInterval(() => {
      const now = Date.now() / 1000;
      const elapsed = intervalStartRef.current
        ? now - intervalStartRef.current
        : 0;

      if (boxIsConnected?.is_connected) {
        setLoader(false);
        setReconnectMode(false); // reset reconnexion mode si succès
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }

        return;
      }

      if (elapsed < duration) {
        connection();
        setLoader(true);
      } else {
        setLoader(false);
        setReconnectMode(false); // reset reconnexion mode si échec
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
      }
    }, INTERVAL);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStarted, reconnectMode, connection, boxIsConnected?.is_connected]);

  useEffect(() => {
    // Arrête le loader immédiatement si la connexion est établie
    if (boxIsConnected?.is_connected) {
      setLoader(false);
      setReconnectMode(false);
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxIsConnected?.is_connected]);

  // Polling léger pour garder l'état de connexion à jour même hors tentative active
  useEffect(() => {
    if (isStarted || reconnectMode) return; // ne poll pas si déjà en mode connexion

    const pollId = setInterval(() => {
      connection();
    }, 10000); // toutes les 10s par exemple

    // Vérifie une fois au montage
    connection();

    return () => clearInterval(pollId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStarted, reconnectMode, connection]);

  return (
    <Spin spinning={loader}>
      <Space
        direction="vertical"
        size={12}
        style={{
          border: '0.0625rem solid #CCD8D6',
          width: '100%',
          background: '#FAFAFA',
          borderRadius: '0.25rem',
          padding: '0.75rem',
          marginTop: '0.5rem',
          marginBottom: '1.5rem',
        }}
      >
        <Flex justify="space-between" align="center" style={{ width: '100%' }}>
          <Text
            style={{
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '135%',
            }}
          >
            BOX status
          </Text>

          <Tag
            color={boxIsConnected?.is_connected ? 'green' : 'red'}
            style={{
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '135%',
            }}
          >
            {boxIsConnected?.is_connected ? 'Connected' : 'Disconnected'}
          </Tag>
        </Flex>

        <Button
          style={{ width: '100%' }}
          disabled={boxIsConnected?.is_connected}
          onClick={() => {
            setReconnectMode(true);
          }}
        >
          <Text
            style={{
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '135%',
            }}
          >
            Reconnection with the BOX
          </Text>
        </Button>
      </Space>
    </Spin>
  );
};
